import { useState, type FormEvent } from "react";

import { useAbortableConfirm } from "../lib/useAbortableConfirm";
import { api, ApiError, type BankAccountInput, type PaymentBankAccount } from "../lib/api";
import "./CuentasCobro.css";

/** «****4821» a partir de la CLABE (o del número de cuenta). */
export function maskedAccountNumber(account: Pick<PaymentBankAccount, "clabe" | "account_number" | "clabe_last4" | "account_last4">): string {
  const last4 = account.clabe_last4 ?? account.account_last4 ?? (account.clabe || account.account_number || "").replace(/\s/g, "").slice(-4);
  return last4 ? `****${last4}` : "";
}

/** «Club de la Colina · BBVA ****4821» (sin alias: «BBVA ****4821»). */
export function bankAccountOptionLabel(account: PaymentBankAccount): string {
  if (account.label) return account.label;
  const core = [account.bank_name, maskedAccountNumber(account)].filter(Boolean).join(" ");
  return account.alias ? `${account.alias} · ${core}` : core;
}

const EMPTY_FORM = { alias: "", bank_name: "", beneficiary_name: "", account_number: "", clabe: "", payment_concept: "" };

type FormState = typeof EMPTY_FORM;

function formFromAccount(account: PaymentBankAccount): FormState {
  return {
    alias: account.alias ?? "",
    bank_name: account.bank_name,
    beneficiary_name: account.beneficiary_name,
    account_number: account.account_number ?? "",
    clabe: account.clabe ?? "",
    payment_concept: account.payment_concept ?? "",
  };
}

function payloadFromForm(form: FormState): BankAccountInput {
  return {
    alias: form.alias.trim() || null,
    bank_name: form.bank_name,
    beneficiary_name: form.beneficiary_name,
    account_number: form.account_number.trim() || null,
    clabe: form.clabe.trim() || null,
    payment_concept: form.payment_concept.trim() || null,
  };
}

type Props = {
  token: string;
  accounts: PaymentBankAccount[];
  onReload: () => Promise<void> | void;
  onNotice: (message: string | null) => void;
  onError: (message: string | null) => void;
};

/** Cuentas de cobro F1 — «Cuentas bancarias (pago por transferencia)»:
 * lista con alias, banco, CLABE enmascarada, beneficiario y predeterminada;
 * Hacer predeterminada / Editar / Quitar y «+ Agregar cuenta bancaria». */
export default function BankAccountsSection({ token, accounts, onReload, onNotice, onError }: Props) {
  const { askConfirm } = useAbortableConfirm([]);
  const [mode, setMode] = useState<{ kind: "closed" } | { kind: "create" } | { kind: "edit"; id: number }>({ kind: "closed" });
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  // Quitar una cuenta que usa un torneo con inscripción abierta: elegir otra.
  const [replacing, setReplacing] = useState<{ id: number; tournaments: string[]; replacementId: string } | null>(null);

  function openCreate() {
    setForm(EMPTY_FORM);
    setMode({ kind: "create" });
    setReplacing(null);
  }

  function openEdit(account: PaymentBankAccount) {
    setForm(formFromAccount(account));
    setMode({ kind: "edit", id: account.id });
    setReplacing(null);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    onError(null);
    onNotice(null);
    try {
      if (mode.kind === "edit") {
        await api.payments.updateBankAccount(token, mode.id, payloadFromForm(form));
        onNotice("Cuenta bancaria actualizada. Los pagos ya registrados conservan los datos que se le mostraron al jugador.");
      } else {
        await api.payments.createBankAccount(token, payloadFromForm(form));
        onNotice("Cuenta bancaria agregada. Ya puedes elegirla en cada torneo.");
      }
      setMode({ kind: "closed" });
      setForm(EMPTY_FORM);
      await onReload();
    } catch (err) {
      onError(err instanceof Error ? err.message : "No se pudo guardar la cuenta bancaria.");
    } finally {
      setBusy(false);
    }
  }

  async function handleMakeDefault(account: PaymentBankAccount) {
    setBusy(true);
    onError(null);
    onNotice(null);
    try {
      await api.payments.setDefaultBankAccount(token, account.id);
      onNotice(`«${bankAccountOptionLabel(account)}» es ahora tu cuenta predeterminada.`);
      await onReload();
    } catch (err) {
      onError(err instanceof Error ? err.message : "No se pudo cambiar la cuenta predeterminada.");
    } finally {
      setBusy(false);
    }
  }

  async function deactivate(accountId: number, replacementId: number | null) {
    setBusy(true);
    onError(null);
    onNotice(null);
    try {
      const response = await api.payments.deactivateBankAccount(token, accountId, replacementId);
      const moved = response.data.moved_tournaments;
      setReplacing(null);
      onNotice(
        moved > 0
          ? `Cuenta quitada. ${moved === 1 ? "1 torneo cobra" : `${moved} torneos cobran`} ahora a la cuenta que elegiste.`
          : "Cuenta quitada. Los pagos ya registrados la conservan en su historial.",
      );
      await onReload();
    } catch (err) {
      if (err instanceof ApiError && err.code === "bank_account_in_use") {
        const list = Array.isArray(err.details?.tournaments) ? (err.details.tournaments as { name?: string }[]) : [];
        const others = accounts.filter((item) => item.id !== accountId);
        setReplacing({
          id: accountId,
          tournaments: list.map((item) => item.name ?? "Torneo"),
          replacementId: others[0] ? String(others[0].id) : "",
        });
      } else {
        onError(err instanceof Error ? err.message : "No se pudo quitar la cuenta bancaria.");
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleRemove(account: PaymentBankAccount) {
    if (
      !(await askConfirm(
        `¿Quitar la cuenta «${bankAccountOptionLabel(account)}»?\n\nYa no se podrá elegir en tus torneos. Los pagos ya registrados conservan sus datos.`,
        { acceptLabel: "Sí, quitar cuenta", tone: "danger" },
      ))
    ) {
      return;
    }
    await deactivate(account.id, null);
  }

  const replacementOptions = replacing ? accounts.filter((item) => item.id !== replacing.id) : [];

  return (
    <div className="cobro-cuentas">
      {accounts.length === 0 ? <p className="muted">Aún no hay cuentas bancarias guardadas.</p> : null}
      <ul className="cobro-cuentas__list">
        {accounts.map((account) => (
          <li className="cobro-cuenta" key={account.id}>
            <div className="cobro-cuenta__main">
              <strong>{account.alias || account.bank_name}</strong>
              <span>
                {account.bank_name}
                {maskedAccountNumber(account) ? ` · ${account.clabe ? "CLABE" : "Cuenta"} ${maskedAccountNumber(account)}` : ""}
              </span>
              <span>Beneficiario: {account.beneficiary_name}</span>
              {account.is_default ? (
                <span className="cobro-cuenta__chips">
                  <span className="cobro-chip cobro-chip--default">Predeterminada</span>
                </span>
              ) : null}
            </div>
            <div className="cobro-cuenta__actions">
              {!account.is_default ? (
                <button type="button" className="btn btn--ghost btn--small" onClick={() => void handleMakeDefault(account)} disabled={busy}>
                  Hacer predeterminada
                </button>
              ) : null}
              <button type="button" className="btn btn--ghost btn--small" onClick={() => openEdit(account)} disabled={busy}>
                Editar
              </button>
              <button type="button" className="btn btn--ghost btn--small cobro-cuenta__remove" onClick={() => void handleRemove(account)} disabled={busy}>
                Quitar
              </button>
            </div>
            {replacing && replacing.id === account.id ? (
              <div className="cobro-cuenta__replace" role="alert">
                <p>
                  Esta cuenta recibe los pagos de {replacing.tournaments.length === 1 ? "un torneo" : `${replacing.tournaments.length} torneos`} con inscripción abierta
                  {replacing.tournaments.length ? `: ${replacing.tournaments.join(", ")}` : ""}. Elige a qué cuenta llegan ahora sus pagos.
                </p>
                {replacementOptions.length === 0 ? (
                  <p className="muted">Primero agrega otra cuenta bancaria.</p>
                ) : (
                  <div className="cobro-cuenta__replace-row">
                    <select
                      className="t123-select"
                      aria-label="Cuenta de reemplazo"
                      value={replacing.replacementId}
                      onChange={(e) => setReplacing({ ...replacing, replacementId: e.target.value })}
                    >
                      {replacementOptions.map((item) => (
                        <option key={item.id} value={item.id}>
                          {bankAccountOptionLabel(item)}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="btn btn--primary btn--small"
                      disabled={busy || !replacing.replacementId}
                      onClick={() => void deactivate(replacing.id, Number(replacing.replacementId))}
                    >
                      Mover pagos y quitar
                    </button>
                    <button type="button" className="btn btn--ghost btn--small" onClick={() => setReplacing(null)} disabled={busy}>
                      Cancelar
                    </button>
                  </div>
                )}
              </div>
            ) : null}
            {mode.kind === "edit" && mode.id === account.id ? (
              <BankForm form={form} setForm={setForm} busy={busy} onSubmit={handleSubmit} onCancel={() => setMode({ kind: "closed" })} editing />
            ) : null}
          </li>
        ))}
      </ul>
      {mode.kind === "create" ? (
        <BankForm form={form} setForm={setForm} busy={busy} onSubmit={handleSubmit} onCancel={() => setMode({ kind: "closed" })} />
      ) : (
        <button type="button" className="cobro-cuentas__add" onClick={openCreate} disabled={busy}>
          + Agregar cuenta bancaria
        </button>
      )}
    </div>
  );
}

function BankForm({
  form,
  setForm,
  busy,
  onSubmit,
  onCancel,
  editing = false,
}: {
  form: FormState;
  setForm: (updater: (current: FormState) => FormState) => void;
  busy: boolean;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onCancel: () => void;
  editing?: boolean;
}) {
  return (
    <form className="setup-call-grid setup-call-grid--two admin-payments-bank-form cobro-cuenta-form" onSubmit={onSubmit}>
      <label className="setup-call-form__wide">
        Nombre corto (alias)
        <input
          maxLength={120}
          value={form.alias}
          onChange={(e) => setForm((current) => ({ ...current, alias: e.target.value }))}
          placeholder="Ej. Club de la Colina · inscripciones"
        />
      </label>
      <label>
        Banco
        <input required value={form.bank_name} onChange={(e) => setForm((current) => ({ ...current, bank_name: e.target.value }))} placeholder="BBVA / Banorte / Santander" />
      </label>
      <label>
        Beneficiario
        <input required value={form.beneficiary_name} onChange={(e) => setForm((current) => ({ ...current, beneficiary_name: e.target.value }))} placeholder="Nombre de la persona o empresa" />
      </label>
      <label>
        # Cuenta
        <input value={form.account_number} onChange={(e) => setForm((current) => ({ ...current, account_number: e.target.value }))} placeholder="Cuenta bancaria" />
      </label>
      <label>
        # Cuenta CLABE
        <input inputMode="numeric" value={form.clabe} onChange={(e) => setForm((current) => ({ ...current, clabe: e.target.value }))} placeholder="18 dígitos" />
      </label>
      <label className="setup-call-form__wide">
        Concepto / instrucción para el comprobante
        <textarea rows={3} value={form.payment_concept} onChange={(e) => setForm((current) => ({ ...current, payment_concept: e.target.value }))} placeholder="Ej. Poner nombre del jugador + categoría para identificar el pago." />
      </label>
      {editing ? (
        <p className="muted setup-call-form__wide">Los pagos ya registrados conservan los datos que vio el jugador; el cambio aplica a pagos nuevos.</p>
      ) : null}
      <div className="cobro-cuenta-form__actions setup-call-form__wide">
        <button className="btn btn--primary" type="submit" disabled={busy}>
          {editing ? "Guardar cambios" : "Agregar cuenta bancaria"}
        </button>
        <button className="btn btn--ghost" type="button" onClick={onCancel} disabled={busy}>
          Cancelar
        </button>
      </div>
    </form>
  );
}
